import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Inbox, Clock, Building2, MapPin, UserX, UserCheck, History } from "lucide-react";
import ReassignVacancyDialog from "./ReassignVacancyDialog";
import { formatDateFr } from "../lib/dateFormat";

/**
 * Widget Tableau de bord — Interventions vacantes à combler (admin / manager).
 *
 * Depuis février 2026 : chaque vacance est directement dérivée d'un shift dont
 * le collaborateur est marqué "absent" ou "indisponible" (plus de dépendance à
 * l'Agenda départements). Le widget liste toutes les vacances ouvertes à venir
 * (aujourd'hui + futur) et permet de réaffecter en un clic.
 */
export default function VacantInterventionsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/department-agenda/vacant-to-fill");
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onUpdated = () => load();
    window.addEventListener("plania:absences-updated", onUpdated);
    window.addEventListener("plania:vacancies-updated", onUpdated);
    const id = setInterval(load, 60_000);
    return () => {
      window.removeEventListener("plania:absences-updated", onUpdated);
      window.removeEventListener("plania:vacancies-updated", onUpdated);
      clearInterval(id);
    };
  }, [load]);

  const openReassign = (v) => {
    setSelectedVacancy(v);
    setDialogOpen(true);
  };

  const handleReplaced = () => {
    load();
    window.dispatchEvent(new CustomEvent("plania:vacancies-updated"));
  };

  const isElapsed = (v) => {
    if (!v?.date || !v?.end_time) return false;
    try {
      const end = new Date(`${v.date}T${v.end_time}:00`);
      return !isNaN(end.getTime()) && end.getTime() <= Date.now();
    } catch {
      return false;
    }
  };

  const originLabel = (origin) =>
    origin === "unavailability" ? "Indisponibilité" : "Absence";

  const originBadgeClass = (origin) =>
    origin === "unavailability"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-red-50 text-red-700 border-red-200";

  return (
    <>
      <div className="bg-white border border-zinc-200 rounded-sm" data-testid="vacant-interventions-widget">
        <div className="px-4 sm:px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`h-7 w-7 inline-flex items-center justify-center rounded-sm flex-shrink-0 ${
              items.length > 0 ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-zinc-50 text-zinc-400 border border-zinc-200"
            }`}>
              <Inbox size={14} />
            </span>
            <div className="min-w-0">
              <p className="label-uppercase">Interventions vacantes à combler</p>
              <p className="text-[11px] text-zinc-500 font-mono truncate">
                Shifts dont le collaborateur est absent ou indisponible
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className={`text-[11px] font-mono px-2 py-0.5 rounded-sm ${
                items.length > 0 ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"
              }`}
              data-testid="vacant-count"
            >
              {items.length} à combler
            </span>
            <Link
              to="/historique-vacances"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-zinc-600 hover:text-zinc-900 px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50"
              data-testid="vacant-history-link"
            >
              <History size={11} />
              Consulter historique
            </Link>
          </div>
        </div>

        {loading ? (
          <p className="p-5 text-sm text-zinc-500 font-mono">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="p-5 text-center" data-testid="vacant-empty">
            <p className="text-sm text-zinc-700">Aucune intervention à recombler.</p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {items.map((v) => (
              <li
                key={v.vacancy_id}
                className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
                data-testid={`vacant-${v.vacancy_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${originBadgeClass(v.origin)}`}
                      data-testid={`vacant-origin-${v.vacancy_id}`}
                    >
                      {originLabel(v.origin)}
                    </span>
                    <p className="text-sm font-medium text-zinc-900 truncate">
                      {v.poste || "Shift à combler"}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mt-1 text-[11px] font-mono text-zinc-600">
                    <p className="inline-flex items-center gap-1.5">
                      <Clock size={11} className="text-zinc-400" />
                      {formatDateFr(v.date)} · {v.start_time} → {v.end_time}
                    </p>
                    <p className="inline-flex items-center gap-1.5 truncate">
                      <Building2 size={11} className="text-zinc-400" /> {v.brand_name}
                    </p>
                    {v.site_name && (
                      <p className="inline-flex items-center gap-1.5 truncate">
                        <MapPin size={11} className="text-zinc-400" /> {v.site_name}
                      </p>
                    )}
                  </div>
                  <p
                    className="mt-1.5 text-[11px] text-red-700 inline-flex items-center gap-1.5"
                    data-testid={`vacant-absent-row-${v.vacancy_id}`}
                  >
                    <UserX size={11} />
                    {originLabel(v.origin)} :{" "}
                    <Link
                      to={`/collaborateurs/${v.absent_collaborateur_id}/dossier`}
                      className="font-medium underline-offset-2 hover:underline"
                      data-testid={`vacant-absent-link-${v.vacancy_id}`}
                    >
                      {v.absent_collaborateur_name}
                    </Link>
                  </p>
                </div>
                <div className="flex-shrink-0 self-start">
                  {isElapsed(v) ? (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm bg-zinc-50 text-zinc-500 border border-zinc-200"
                      data-testid={`vacant-elapsed-${v.vacancy_id}`}
                      title="L'heure de fin prévue est dépassée — réaffectation impossible"
                    >
                      Délai dépassé
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openReassign(v)}
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-sm bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
                      data-testid={`vacant-reassign-${v.vacancy_id}`}
                    >
                      <UserCheck size={11} />
                      Réaffecter
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ReassignVacancyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        vacancy={selectedVacancy}
        onReplaced={handleReplaced}
      />
    </>
  );
}
